/**
 * SEO-анализ страницы: типы ответа API, запрос через наш домен и перевод.
 *
 * Анализатор (seokar) пишет замечания по-английски, а инструмент живёт
 * только на русской витрине. Перевод по тексту замечания, а не по
 * порядковому номеру: API обещает, что `message` стабилен в пределах
 * версии анализатора. Незнакомое замечание показывается как есть, чтобы
 * новая версия анализатора не прятала находки, а просто говорила
 * по-английски до следующей правки таблицы.
 */

import { gruppirovatW3c, podpisStrok } from '@/lib/w3c-perevod';

export type UrovenZamechaniya = 'critical' | 'error' | 'warning' | 'info' | 'good';

export interface ZamechanieSeo {
  /** Уровень: critical, error, warning, info, good. Строка, а не перечень: новый уровень анализатора не должен ломать разбор. */
  level: string;
  element: string;
  message: string;
  details?: string;
  recommendation?: string;
  /**
   * Сколько одинаковых замечаний склеено в это. Нет — замечание одно.
   * Анализатор повторяет некоторые слово в слово (у stripe.com «H1 Too
   * Long» дважды), и API отдаёт их одной строкой со счётчиком.
   */
  count?: number;
}

/** Название замечания с числом повторов: «H1 слишком длинный (2)». */
export function sPovtorami(nazvanie: string, z: Pick<ZamechanieSeo, 'count'>): string {
  return z.count && z.count > 1 ? `${nazvanie} (${String(z.count)})` : nazvanie;
}

export interface OtchetSeo {
  url: string;
  requested_url: string;
  status_code: number;
  page_bytes: number;
  fetch_ms: number;
  proxy_country?: string;
  checked_at: string;
  score: number;
  counts: { critical: number; errors: number; warnings: number; notices: number; passed: number };
  meta: {
    title: string;
    title_length: number;
    description: string;
    description_length: number;
    canonical: string;
    robots: string[] | null;
    viewport: string;
    charset: string;
    lang: string;
    favicon: string;
  };
  headings: {
    levels: Record<string, string[]> | null;
    counts: Record<string, number> | null;
    total: number;
  };
  content: {
    chars: number;
    text_to_html_percent: number;
    readability?: number;
    paragraphs: number;
    has_video: boolean;
    has_tables: boolean;
    keywords: { phrase: string; percent: number }[] | null;
  };
  images: {
    total: number;
    with_alt: number;
    missing_alt: number;
    empty_alt: number;
    long_alt: number;
    decorative: number;
  };
  links: {
    total: number;
    internal: number;
    external: number;
    nofollow_internal: number;
    nofollow_external: number;
    /** Доли анкоров, %: generic, branded, keyword_like, descriptive. */
    anchors?: Record<string, number> | null;
  };
  social: { open_graph: Record<string, string> | null; twitter: Record<string, string> | null };
  structured_data: { types: string[] | null; json_ld: number; microdata: number; rdfa: number };
  issues: ZamechanieSeo[] | null;
  validation: {
    available: boolean;
    errors_total: number;
    warnings_total: number;
    infos_total?: number;
    errors?: SoobshchenieValidatora[] | null;
    warnings?: SoobshchenieValidatora[] | null;
    infos?: SoobshchenieValidatora[] | null;
  };
}

export interface SoobshchenieValidatora {
  message: string;
  line?: number;
  extract?: string;
}

export interface OstatokSeo {
  signed_in: boolean;
  free_per_day: number;
  free_left: number;
  /** Цена платной проверки в минорных единицах валюты кошелька. */
  price?: number;
  currency?: string;
}

export interface OtvetSeo {
  report: OtchetSeo;
  charged: number;
  currency?: string;
  quota: OstatokSeo;
}

/**
 * Причина отказа, от которой зависит следующее действие человека.
 *
 * `oplata` — бесплатные кончились, нужна явная команда заплатить (цена
 * приходит вместе с отказом). `balans` — платить нечем.
 */
export type OshibkaSeo =
  | 'adres'
  | 'chastnyj'
  | 'gost-limit'
  | 'oplata'
  | 'balans'
  | 'ne-skachat'
  | 'ne-html'
  | 'bolshaya'
  | 'net-proksi'
  | 'servis'
  | 'set';

export type RezultatSeo =
  | { ok: true; otvet: OtvetSeo }
  | { ok: false; oshibka: OshibkaSeo; cena?: { amount: number; currency: string } };

/** Код API в причину для формы. Незнакомое — «сервис недоступен». */
export function prichinaPoKodu(code: string | undefined, status: number): OshibkaSeo {
  switch (code) {
    case 'bad_url':
      return 'adres';
    case 'private_url':
      return 'chastnyj';
    case 'guest_quota_exceeded':
      return 'gost-limit';
    case 'seo_payment_required':
      return 'oplata';
    case 'insufficient_balance':
    case 'quota_exceeded_insufficient_balance':
      return 'balans';
    case 'fetch_failed':
      return 'ne-skachat';
    case 'not_html':
      return 'ne-html';
    case 'page_too_large':
      return 'bolshaya';
    case 'no_proxies':
      return 'net-proksi';
    default:
      // 401 без кода гостя — это «нас не признали своими», а не предел.
      if (status === 422) return 'ne-skachat';
      return 'servis';
  }
}

/** Текст причины для человека. */
export const TEKST_OSHIBKI: Record<OshibkaSeo, string> = {
  adres: 'Это не похоже на адрес страницы. Вставьте ссылку вида https://site.ru/page.',
  chastnyj: 'Этот адрес недоступен из интернета. Проверяются только публичные страницы.',
  'gost-limit': 'Бесплатные проверки на сегодня закончились.',
  oplata: 'Бесплатные проверки на сегодня закончились.',
  balans: 'На счёте не хватает денег на проверку. Пополните счёт, чтобы продолжить.',
  'ne-skachat':
    'Не удалось скачать страницу: сайт не ответил или закрыт от роботов. Попытка не засчитана.',
  'ne-html': 'По этому адресу не HTML-страница. Проверяются только веб-страницы.',
  bolshaya: 'Страница слишком большая для проверки.',
  'net-proksi': 'Проверка сейчас занята. Попробуйте через минуту, попытка не засчитана.',
  servis: 'SEO-анализ временно не работает. Попробуйте позже.',
  set: 'Не получилось связаться с сервером. Проверьте связь и попробуйте ещё раз.',
};

/** Разбор ответа нашей прокладки. */
export async function razobratOtvet(otvet: Response): Promise<RezultatSeo> {
  const telo = (await otvet.json().catch(() => ({}))) as {
    error?: { code?: string; price?: { amount?: number; currency?: string } };
  } & Partial<OtvetSeo>;

  if (otvet.ok && telo.report) return { ok: true, otvet: telo as OtvetSeo };

  const oshibka = prichinaPoKodu(telo.error?.code, otvet.status);
  const price = telo.error?.price;
  if (oshibka === 'oplata' && price?.amount && price.currency) {
    return { ok: false, oshibka, cena: { amount: price.amount, currency: price.currency } };
  }
  return { ok: false, oshibka };
}

/** Проверить страницу. `paid` — человек согласился заплатить за эту проверку. */
export async function proveritStranicu(
  url: string,
  paid: boolean,
  signal?: AbortSignal,
): Promise<RezultatSeo> {
  try {
    const otvet = await fetch('/api/seo/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paid ? { url, paid: true } : { url }),
      signal,
    });
    return await razobratOtvet(otvet);
  } catch {
    return { ok: false, oshibka: 'set' };
  }
}

/** Остаток бесплатных проверок. `null` — узнать не удалось, форма работает и без него. */
export async function uznatOstatok(): Promise<OstatokSeo | null> {
  try {
    const otvet = await fetch('/api/seo/quota', { cache: 'no-store' });
    if (!otvet.ok) return null;
    return (await otvet.json()) as OstatokSeo;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Перевод замечаний                                                    */
/* ------------------------------------------------------------------ */

/** Замечание по-русски: название и совет. */
export interface Perevod {
  nazvanie: string;
  sovet?: string;
}

const ZAMECHANIYA: Record<string, Perevod> = {
  'Invalid JSON-LD Top-Level Structure': {
    nazvanie: 'Неверная структура JSON-LD',
    sovet: 'Верхний уровень JSON-LD должен быть объектом или массивом объектов.',
  },
  'Empty JSON-LD Script Tag': {
    nazvanie: 'Пустой блок JSON-LD',
    sovet: 'Удалите пустой тег или заполните разметку.',
  },
  'JSON-LD Parsing Error': {
    nazvanie: 'Ошибка в JSON-LD',
    sovet: 'Проверьте разметку валидатором schema.org.',
  },
  'Missing Title Tag': {
    nazvanie: 'Нет тега title',
    sovet: 'Добавьте уникальный title длиной 30–60 знаков.',
  },
  'Title Too Short': {
    nazvanie: 'Title слишком короткий',
    sovet: 'Сделайте title 30–60 знаков с главным запросом.',
  },
  'Title Too Long': {
    nazvanie: 'Title слишком длинный',
    sovet: 'Сократите title до 60 знаков, поисковик обрежет хвост.',
  },
  'Optimal Title Length': { nazvanie: 'Длина title в норме' },
  'Missing Meta Description': {
    nazvanie: 'Нет meta description',
    sovet: 'Добавьте описание 120–160 знаков: из него собирается сниппет.',
  },
  'Meta Description Too Short': {
    nazvanie: 'Description слишком короткий',
    sovet: 'Расширьте описание до 120–160 знаков.',
  },
  'Meta Description Too Long': {
    nazvanie: 'Description слишком длинный',
    sovet: 'Сократите описание до 160 знаков.',
  },
  'Optimal Meta Description Length': { nazvanie: 'Длина description в норме' },
  'Meta Robots Not Specified': {
    nazvanie: 'Meta robots не задан',
    sovet: 'По умолчанию страница индексируется, это нормально.',
  },
  'Page is NoIndexed': {
    nazvanie: 'Страница закрыта от индексации (noindex)',
    sovet: 'Уберите noindex, если страница должна быть в поиске.',
  },
  'Page is NoFollow': {
    nazvanie: 'Ссылки закрыты nofollow',
    sovet: 'Уберите nofollow, чтобы передавать вес по ссылкам.',
  },
  'Missing Canonical URL': {
    nazvanie: 'Нет canonical',
    sovet: 'Укажите канонический адрес, чтобы избежать дублей.',
  },
  'Canonical URL Mismatch': {
    nazvanie: 'Canonical ведёт на другой адрес',
    sovet: 'Убедитесь, что это намеренно: иначе поисковик проиндексирует другую страницу.',
  },
  'Canonical URL Matches Page URL': { nazvanie: 'Canonical совпадает с адресом страницы' },
  'Canonical URL Present (No Page URL for Comparison)': { nazvanie: 'Canonical указан' },
  'Missing Viewport Meta Tag': {
    nazvanie: 'Нет meta viewport',
    sovet: 'Добавьте <meta name="viewport" content="width=device-width, initial-scale=1">.',
  },
  'Suboptimal Viewport Configuration': {
    nazvanie: 'Viewport настроен не лучшим образом',
    sovet: 'Используйте width=device-width, initial-scale=1.',
  },
  'Viewport Configured for Mobile': { nazvanie: 'Страница адаптирована под телефоны' },
  'Charset Not Declared': {
    nazvanie: 'Не указана кодировка',
    sovet: 'Добавьте <meta charset="utf-8"> в начало head.',
  },
  'Non-UTF-8 Charset Used': {
    nazvanie: 'Кодировка не UTF-8',
    sovet: 'Переведите страницу на UTF-8.',
  },
  'UTF-8 Charset Declared': { nazvanie: 'Кодировка UTF-8' },
  'HTML Language Not Declared': {
    nazvanie: 'Не указан язык страницы',
    sovet: 'Добавьте атрибут lang у тега html, например lang="ru".',
  },
  'HTML Language Declared': { nazvanie: 'Язык страницы указан' },
  'Favicon Not Detected or Specified': {
    nazvanie: 'Не найден favicon',
    sovet: 'Добавьте иконку сайта: она видна в выдаче.',
  },
  'Favicon Link/Default Resolved': { nazvanie: 'Favicon найден' },
  'No Headings Found': {
    nazvanie: 'На странице нет заголовков',
    sovet: 'Разметьте структуру заголовками H1–H3.',
  },
  'Missing H1 Tag': {
    nazvanie: 'Нет заголовка H1',
    sovet: 'Добавьте один H1 с главной темой страницы.',
  },
  'Single H1 Tag Present': { nazvanie: 'Один H1 на странице' },
  'Heading Structure Starts Incorrectly': {
    nazvanie: 'Структура заголовков начинается не с H1',
    sovet: 'Первым заголовком страницы должен идти H1.',
  },
  'Skipped Heading Level': {
    nazvanie: 'Пропущен уровень заголовка',
    sovet: 'Не перескакивайте уровни: после H2 идёт H3, а не H4.',
  },
  'Logical Heading Hierarchy': { nazvanie: 'Иерархия заголовков логичная' },
  'No Main Content Text Extracted': {
    nazvanie: 'Не найден основной текст',
    sovet: 'Возможно, текст подгружается скриптом. Отдавайте его в HTML.',
  },
  'Thin Content (Low Word Count)': {
    nazvanie: 'Мало текста',
    sovet: 'Расширьте текст: короткие страницы ранжируются хуже.',
  },
  'Sufficient Content Length': { nazvanie: 'Текста достаточно' },
  'Low Text-to-HTML Ratio': {
    nazvanie: 'Мало текста относительно кода',
    sovet: 'Уменьшите лишнюю разметку и встроенные скрипты или добавьте текст.',
  },
  'Acceptable Text-to-HTML Ratio': { nazvanie: 'Доля текста в коде в норме' },
  'Very Low Readability Score': {
    nazvanie: 'Текст очень трудно читать',
    sovet: 'Упростите: короче предложения, меньше длинных слов.',
  },
  'Low to Moderate Readability Score': {
    nazvanie: 'Текст читается с трудом',
    sovet: 'Разбейте длинные предложения.',
  },
  'Good Readability Score': { nazvanie: 'Текст читается легко' },
  'Readability Not Assessed (Short Content)': { nazvanie: 'Читаемость не оценена: текста мало' },
  'No Images Found on Page': { nazvanie: 'На странице нет картинок' },
  'Missing Alt Attribute': {
    nazvanie: 'У картинок нет alt',
    sovet: 'Опишите картинки в alt: так их находит поиск по картинкам.',
  },
  'Decorative Image Correctly Marked': { nazvanie: 'Декоративные картинки помечены верно' },
  'Empty Alt Text for Potentially Non-Decorative Image': {
    nazvanie: 'Пустой alt у содержательной картинки',
    sovet: 'Заполните alt, если картинка несёт смысл.',
  },
  'Alt Text Too Long': { nazvanie: 'Слишком длинный alt', sovet: 'Сократите alt до 125 знаков.' },
  'Good Image Alt Text Coverage': { nazvanie: 'У картинок заполнен alt' },
  'Some Images Have Empty Alt Text': { nazvanie: 'У части картинок пустой alt' },
  'No Internal Links Found': {
    nazvanie: 'Нет внутренних ссылок',
    sovet: 'Добавьте ссылки на другие страницы сайта: так передаётся вес.',
  },
  'High Ratio of External Links': {
    nazvanie: 'Много внешних ссылок',
    sovet: 'Проверьте, что все внешние ссылки нужны.',
  },
  'Missing Essential Open Graph Tags': {
    nazvanie: 'Нет основных тегов Open Graph',
    sovet:
      'Добавьте og:title, og:type, og:image и og:url: из них строится превью в соцсетях и мессенджерах.',
  },
  'Missing Recommended Open Graph Description': {
    nazvanie: 'Нет og:description',
    sovet: 'Добавьте описание для превью.',
  },
  'Essential Open Graph Tags Present': { nazvanie: 'Теги Open Graph на месте' },
  'Missing Twitter Card Type': {
    nazvanie: 'Не указан twitter:card',
    sovet: 'Добавьте twitter:card, например summary_large_image.',
  },
  'Potentially Incomplete Twitter Card Tags': { nazvanie: 'Теги Twitter Card неполные' },
  'Twitter Card Tags Appear Configured': { nazvanie: 'Теги Twitter Card настроены' },
  'No Twitter Card Tags Found': { nazvanie: 'Нет тегов Twitter Card' },
  'No Common Structured Data Detected': {
    nazvanie: 'Нет микроразметки',
    sovet: 'Добавьте schema.org (JSON-LD): она даёт расширенные сниппеты.',
  },
  'JSON-LD Data Found': { nazvanie: 'Найдена разметка JSON-LD' },
  'Microdata Found': { nazvanie: 'Найдена микроразметка Microdata' },
  'RDFa Data Found': { nazvanie: 'Найдена разметка RDFa' },
};

/** Перевести замечание. Незнакомое возвращается как есть. */
export function perevesti(message: string): Perevod {
  const tochno = ZAMECHANIYA[message];
  if (tochno) return tochno;

  // Замечания с числом или уровнем внутри текста.
  let m = /^Multiple H1 Tags \((\d+)\)$/.exec(message);
  if (m)
    return { nazvanie: `Несколько H1 (${m[1] ?? ''})`, sovet: 'Оставьте на странице один H1.' };
  m = /^(H[1-6]) Too Long$/.exec(message);
  if (m)
    return {
      nazvanie: `Слишком длинные ${m[1] ?? ''}`,
      sovet: 'Сократите заголовок до 70 знаков.',
    };
  m = /^(H[1-6]) Too Wordy$/.exec(message);
  if (m)
    return {
      nazvanie: `Многословные ${m[1] ?? ''}`,
      sovet: 'Сделайте заголовок короче и конкретнее.',
    };
  m = /^(\d+) Internal Nofollow Links$/.exec(message);
  if (m)
    return {
      nazvanie: `Внутренних ссылок с nofollow: ${m[1] ?? ''}`,
      sovet: 'Внутренние ссылки обычно не закрывают nofollow.',
    };

  return { nazvanie: message };
}

/**
 * Подробность замечания по-русски.
 *
 * В подробности сидят цифры и адреса конкретной страницы (длина
 * title, какая картинка без alt), и без них замечание не говорит,
 * что именно править. Шаблоны взяты из seokar; незнакомый текст
 * с цифрами возвращается как есть: английская строка лучше потерянной цифры.
 */
const PODROBNOSTI: [RegExp, (m: RegExpExecArray) => string][] = [
  [/^Length (\d+) is optimal\.$/, (m) => `Длина ${g(m, 1)} зн., в норме.`],
  [
    /^Length (\d+) \(optimal (\d+)-(\d+)\)\.$/,
    (m) => `Длина ${g(m, 1)} зн., норма ${g(m, 2)}–${g(m, 3)}.`,
  ],
  [
    /^Page URL \('(.*)'\) differs from canonical \('(.*)'\)\.$/,
    (m) => `Адрес страницы ${g(m, 1)}, а canonical ведёт на ${g(m, 2)}.`,
  ],
  [/^Canonical URL found: (.*)\.$/, (m) => `Canonical: ${g(m, 1)}.`],
  [/^Content: '(.*)'\. Might not be optimal\.$/, (m) => `Сейчас: ${g(m, 1)}.`],
  [/^Charset is '(.*)'\./, (m) => `Кодировка ${g(m, 1)}.`],
  [/^HTML language declared as '(.*)'\.$/, (m) => `Язык: ${g(m, 1)}.`],
  [/^A favicon URL was resolved: (.*)$/, (m) => `Иконка: ${g(m, 1)}`],
  [/^The page has (\d+) H1 tags\./, (m) => `Заголовков H1 на странице: ${g(m, 1)}.`],
  [
    /^The heading structure begins with H(\d)/,
    (m) => `Структура начинается с H${g(m, 1)}, а не с H1.`,
  ],
  [
    /^Heading level H(\d) appears to be missing between H(\d) and H(\d)\.$/,
    (m) => `Между H${g(m, 2)} и H${g(m, 3)} пропущен H${g(m, 1)}.`,
  ],
  [
    /^The (H\d) text '(.*)' is (\d+) characters long\.$/,
    (m) => `${g(m, 1)} «${g(m, 2)}»: ${g(m, 3)} зн.`,
  ],
  [
    /^The (H\d) text '(.*)' contains (\d+) words\.$/,
    (m) => `${g(m, 1)} «${g(m, 2)}»: ${g(m, 3)} слов.`,
  ],
  [
    /^Main content length is approximately (\d+) characters \(minimum recommended: (\d+)\)\.$/,
    (m) => `Основного текста около ${g(m, 1)} зн., рекомендуется не меньше ${g(m, 2)}.`,
  ],
  [/^Main content length \((\d+) chars\)/, (m) => `Основного текста ${g(m, 1)} зн.`],
  [/^Text-to-HTML ratio (?:is )?\(?([\d.]+)%/, (m) => `Текст занимает ${g(m, 1)}% кода страницы.`],
  [/^Flesch Reading Ease score is ([\d.-]+)/, (m) => `Индекс читаемости Флеша: ${g(m, 1)} из 100.`],
  [
    /^Image '(.*)\.\.\.' is missing the 'alt' attribute/,
    (m) => `Картинка ${g(m, 1)} без атрибута alt.`,
  ],
  [
    /^Image '(.*)\.\.\.' has an empty alt attribute \(alt=""\) and is marked/,
    (m) => `Картинка ${g(m, 1)} помечена декоративной.`,
  ],
  [/^Image '(.*)\.\.\.' has an empty alt attribute/, (m) => `Картинка ${g(m, 1)} с пустым alt.`],
  [
    /^The alt text for image '(.*)\.\.\.' is (\d+) characters long \(max recommended: (\d+)\)\.$/,
    (m) => `У картинки ${g(m, 1)} alt длиной ${g(m, 2)} зн., рекомендуется до ${g(m, 3)}.`,
  ],
  [
    /^(\d+) image\(s\) have empty alt text/,
    (m) => `Картинок с пустым alt без пометки «декоративная»: ${g(m, 1)}.`,
  ],
  [
    /^The page has (\d+) external links out of (\d+) total links\./,
    (m) => `Внешних ссылок ${g(m, 1)} из ${g(m, 2)}.`,
  ],
  [/^Missing or empty essential OG tags: (.*?)\. /, (m) => `Не хватает: ${g(m, 1)}.`],
  [/^Twitter Card type '(.*)' is specified/, (m) => `Тип карточки: ${g(m, 1)}.`],
  [/^(\d+) JSON-LD script block/, (m) => `Блоков JSON-LD: ${g(m, 1)}.`],
  [/^(\d+) Microdata item/, (m) => `Элементов Microdata: ${g(m, 1)}.`],
  [/^(\d+) RDFa item/, (m) => `Элементов RDFa: ${g(m, 1)}.`],
];

function g(m: RegExpExecArray, i: number): string {
  return m[i] ?? '';
}

/**
 * Подробности seokar без данных страницы, дословно.
 *
 * Цифр страницы в них нет, но есть цифры в словах («UTF-8», «H1»,
 * «H1-H6»), и запасной ход ниже принимал их за полезные данные и
 * выводил по-английски.
 */
const PODROBNOSTI_TOCHNO: Record<string, string | null> = {
  'Page uses UTF-8 character encoding.': null,
  'Page has a single H1 tag, which is generally the recommended practice for the main page heading.':
    null,
  'The page does not contain any heading tags (H1-H6).':
    'На странице нет ни одного заголовка H1–H6.',
  'The page is missing an H1 tag, which is critical for defining the main topic.':
    'Без H1 поисковику труднее понять главную тему страницы.',
  'The heading structure appears logical and hierarchical without any skipped levels.': null,
  "The <title> tag is crucial; it's missing.": 'Тега <title> нет.',
  'Meta description missing.': 'Тега description нет.',
  "Defaults to 'index, follow'.": 'По умолчанию страница индексируется, ссылки учитываются.',
  "'noindex' present, prevents indexing.": 'Стоит noindex: поисковики не покажут страницу.',
  "'nofollow' present, links won't be followed.":
    'Стоит nofollow: ссылки со страницы не учитываются.',
  'No canonical URL specified. Risk of duplicate content.':
    'Canonical не указан, поисковик может счесть страницу дублем.',
  'Canonical URL correctly points to the current page.': null,
  'Viewport meta tag is missing, crucial for mobile responsiveness.':
    'Без viewport страница на телефоне показывается уменьшенной копией.',
  'Viewport appears correctly configured.': null,
  'Character encoding missing. Can cause display issues.':
    'Кодировка не указана, текст может отображаться «кракозябрами».',
  'Language not declared in <html> tag. Important for accessibility and SEO.':
    'Язык в <html lang> не указан.',
  'No specific favicon link tag found, and default /favicon.ico might not be intended or exist.':
    'Тега иконки нет, остаётся надеяться на /favicon.ico.',
  'Could not extract significant textual content from main content selectors or body.':
    'Текста в HTML почти нет: вероятно, он подгружается скриптом.',
  'Content too short for a reliable readability score.': null,
  'No <img> tags were found in the HTML content. Images can enhance user engagement.':
    'Картинок на странице нет.',
  'All detected images appear to have appropriate alt text attributes or are correctly marked as decorative.':
    null,
  'The page does not appear to have any internal links to other pages on the same site.':
    'Ссылок на другие страницы сайта нет.',
  "The og:description tag is missing or empty. It's highly recommended for a more complete social snippet.":
    'Нет og:description: превью в соцсетях будет без описания.',
  "The 'twitter:card' tag, specifying the card type (e.g., 'summary', 'summary_large_image'), is missing or empty.":
    'Нет тега twitter:card с типом карточки.',
  'Twitter Card type is specified, but other important tags like twitter:title or twitter:image might be missing or empty.':
    'Тип карточки есть, но может не хватать twitter:title или twitter:image.',
  'No Twitter Card meta tags were found on the page.': 'Тегов Twitter Card нет.',
  'No common structured data formats (JSON-LD, Microdata, RDFa) were found on the page.':
    'Ни JSON-LD, ни Microdata, ни RDFa на странице нет.',
  'Essential Open Graph tags (title, type, image, url) are present and have content.': null,
  'Parsed JSON-LD content is not a valid JSON object or an array of JSON objects.':
    'JSON-LD разобран, но это не объект и не массив объектов.',
  "An empty <script type='application/ld+json'> tag was found.": 'Есть пустой блок JSON-LD.',
};

/**
 * Подробность с цифрами страницы или `undefined`, если в ней нет ничего
 * сверх названия (такие фразы только повторяли бы его по-английски).
 */
export function podrobnost(details: string | undefined): string | undefined {
  if (!details) return undefined;
  const tochno = PODROBNOSTI_TOCHNO[details];
  if (tochno !== undefined) return tochno ?? undefined;
  for (const [re, f] of PODROBNOSTI) {
    const m = re.exec(details);
    if (m) return f(m);
  }
  return /\d|https?:\/\//.test(details) ? details : undefined;
}

/** Виды анкоров по-русски. */
export const ANKORY: Record<string, string> = {
  keyword_like: 'Короткие фразы',
  descriptive: 'Развёрнутые фразы',
  branded: 'С названием сайта',
  generic: 'Общие («подробнее», «здесь»)',
};

/** Порядок и подписи уровней. */
export const UROVNI: { level: UrovenZamechaniya; podpis: string }[] = [
  { level: 'critical', podpis: 'Критично' },
  { level: 'error', podpis: 'Ошибки' },
  { level: 'warning', podpis: 'Предупреждения' },
  { level: 'info', podpis: 'К сведению' },
  { level: 'good', podpis: 'В порядке' },
];

/** Замечания, сгруппированные по уровню в порядке важности. */
export function poUrovnyam(
  issues: ZamechanieSeo[] | null | undefined,
): { level: UrovenZamechaniya; podpis: string; spisok: ZamechanieSeo[] }[] {
  const vse = issues ?? [];
  return UROVNI.map((u) => ({ ...u, spisok: vse.filter((z) => z.level === u.level) })).filter(
    (g) => g.spisok.length > 0,
  );
}

/** Оценка словами. */
export function ocenka(score: number): 'horosho' | 'sredne' | 'ploho' {
  if (score >= 80) return 'horosho';
  if (score >= 50) return 'sredne';
  return 'ploho';
}

/**
 * Отчёт текстом Markdown для истории.
 *
 * История общая с остальными инструментами и рисует записи через тот
 * же вывод Markdown, поэтому сохраняем не JSON, а читаемый отчёт.
 */
export function vMarkdown(r: OtchetSeo): string {
  const stroki: string[] = [
    `## SEO-анализ: ${r.url}`,
    '',
    `**Оценка: ${String(r.score)} из 100**`,
    '',
  ];
  for (const g of poUrovnyam(r.issues)) {
    stroki.push(`### ${g.podpis}`, '');
    for (const z of g.spisok) {
      const p = perevesti(z.message);
      const pod = podrobnost(z.details);
      stroki.push(
        `- **${sPovtorami(p.nazvanie, z)}**${pod ? ` ${pod}` : ''}${p.sovet ? ` ${p.sovet}` : ''}`,
      );
    }
    stroki.push('');
  }
  for (const razdel of razdely(r)) {
    stroki.push(`### ${razdel.zagolovok}`, '', '| Параметр | Значение |', '| --- | --- |');
    for (const [k, v] of razdel.stroki) stroki.push(`| ${yachejka(k)} | ${yachejka(v)} |`);
    stroki.push('');
  }
  return stroki.join('\n').trim();
}

function yachejka(s: string): string {
  const t = s.replace(/\|/g, '/').replace(/\n/g, ' / ').replace(/\s+/g, ' ').trim();
  return t === '' ? '—' : t;
}

/** Раздел отчёта: заголовок и строки «параметр — значение». */
export interface Razdel {
  zagolovok: string;
  stroki: [string, string][];
}

const DA_NET = (b: boolean) => (b ? 'есть' : 'нет');

function ili(s: string | undefined | null): string {
  return s && s.trim() !== '' ? s : '—';
}

function chislo(n: number): string {
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

/**
 * Все данные анализатора разделами.
 *
 * Общие для экрана и для истории: всё, что пришло от API, должно быть
 * видно человеку. Раньше экран показывал десяток строк сводки, а
 * заголовки второго уровня, частые слова, теги соцсетей, анкоры и
 * замечания валидатора терялись, хотя API их отдавал.
 */
export function razdely(r: OtchetSeo): Razdel[] {
  const out: Razdel[] = [];

  out.push({
    zagolovok: 'Мета-теги',
    stroki: [
      ['Title', `${ili(r.meta.title)} (${String(r.meta.title_length)} зн.)`],
      ['Description', `${ili(r.meta.description)} (${String(r.meta.description_length)} зн.)`],
      ['Canonical', ili(r.meta.canonical)],
      ['Robots', ili((r.meta.robots ?? []).join(', '))],
      ['Viewport', ili(r.meta.viewport)],
      ['Кодировка', ili(r.meta.charset)],
      ['Язык', ili(r.meta.lang)],
      ['Favicon', ili(r.meta.favicon)],
    ],
  });

  const zag: [string, string][] = [['Всего заголовков', String(r.headings.total)]];
  for (const u of ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']) {
    const teksty = r.headings.levels?.[u] ?? [];
    const vsego = r.headings.counts?.[u] ?? teksty.length;
    if (vsego === 0) continue;
    const eshche = vsego > teksty.length ? `\n…и ещё ${String(vsego - teksty.length)}` : '';
    zag.push([`${u.toUpperCase()} (${String(vsego)})`, teksty.join('\n') + eshche]);
  }
  out.push({ zagolovok: 'Заголовки', stroki: zag });

  const kontent: [string, string][] = [
    ['Объём текста', `${chislo(r.content.chars)} зн.`],
    ['Доля текста в коде', `${chislo(r.content.text_to_html_percent)}%`],
    // Индекс Флеша считается по английской формуле, и API отдаёт его
    // только для английских страниц. Для остальных строки нет совсем:
    // прочерк выглядел бы как «не удалось посчитать».
    ...(r.content.readability === undefined
      ? []
      : ([
          ['Читаемость (индекс Флеша, для английского)', `${chislo(r.content.readability)} из 100`],
        ] as [string, string][])),
    ['Абзацев', String(r.content.paragraphs)],
    ['Видео', DA_NET(r.content.has_video)],
    ['Таблицы', DA_NET(r.content.has_tables)],
  ];
  const slova = r.content.keywords ?? [];
  if (slova.length > 0) {
    kontent.push([
      'Частые слова',
      slova.map((k) => `${k.phrase} — ${chislo(k.percent)}%`).join('\n'),
    ]);
  }
  out.push({ zagolovok: 'Контент', stroki: kontent });

  out.push({
    zagolovok: 'Картинки',
    stroki: [
      ['Всего', String(r.images.total)],
      ['С alt', String(r.images.with_alt)],
      ['Без alt', String(r.images.missing_alt)],
      ['С пустым alt', String(r.images.empty_alt)],
      ['Со слишком длинным alt', String(r.images.long_alt)],
      ['Помечены декоративными', String(r.images.decorative)],
    ],
  });

  const ssylki: [string, string][] = [
    ['Всего', String(r.links.total)],
    ['Внутренних', String(r.links.internal)],
    ['Внешних', String(r.links.external)],
    ['Внутренних с nofollow', String(r.links.nofollow_internal)],
    ['Внешних с nofollow', String(r.links.nofollow_external)],
  ];
  const ankory = Object.entries(r.links.anchors ?? {}).sort((a, b) => b[1] - a[1]);
  if (ankory.length > 0) {
    ssylki.push([
      'Тексты ссылок',
      ankory.map(([k, v]) => `${ANKORY[k] ?? k} — ${chislo(v)}%`).join('\n'),
    ]);
  }
  out.push({ zagolovok: 'Ссылки', stroki: ssylki });

  const og = Object.entries(r.social.open_graph ?? {});
  const tw = Object.entries(r.social.twitter ?? {});
  out.push({
    zagolovok: 'Соцсети и мессенджеры',
    stroki: og.length + tw.length === 0 ? [['Теги', 'нет']] : [...og, ...tw],
  });

  out.push({
    zagolovok: 'Микроразметка',
    stroki: [
      ['Типы schema.org', ili((r.structured_data.types ?? []).join(', '))],
      ['JSON-LD', String(r.structured_data.json_ld)],
      ['Microdata', String(r.structured_data.microdata)],
      ['RDFa', String(r.structured_data.rdfa)],
    ],
  });

  const v = r.validation;
  if (v.available) {
    const val: [string, string][] = [
      ['Ошибок', String(v.errors_total)],
      ['Предупреждений', String(v.warnings_total)],
      ['Замечаний', String(v.infos_total ?? 0)],
    ];
    // Одинаковые сообщения одной строкой с числом повторов: у сайтов на
    // Next.js «косая черта в пустом теге» приходит по разу на каждый
    // тег, и 35 одинаковых строк прятали под собой настоящие ошибки.
    const spisok = (vid: string, s: SoobshchenieValidatora[] | null | undefined) => {
      for (const gr of gruppirovatW3c(s)) {
        const gde = podpisStrok(gr.stroki);
        const raz = gr.raz > 1 ? ` ×${String(gr.raz)}` : '';
        val.push([`${vid}${raz}${gde ? `, ${gde}` : ''}`, gr.tekst]);
      }
    };
    spisok('Ошибка', v.errors);
    spisok('Предупреждение', v.warnings);
    spisok('Замечание', v.infos);
    out.push({ zagolovok: 'Валидация HTML (W3C)', stroki: val });
  } else {
    out.push({ zagolovok: 'Валидация HTML (W3C)', stroki: [['Проверка', 'валидатор не ответил']] });
  }

  out.push({
    zagolovok: 'Загрузка страницы',
    stroki: [
      ['Проверенный адрес', r.url],
      ...(r.requested_url && r.requested_url !== r.url
        ? ([['Введённый адрес', r.requested_url]] as [string, string][])
        : []),
      ['Ответ сервера', String(r.status_code)],
      ['Размер HTML', `${chislo(Math.round(r.page_bytes / 1024))} КБ`],
      ['Скачано за', `${chislo(r.fetch_ms / 1000)} с`],
      ['Страна прокси', ili(r.proxy_country)],
    ],
  });

  return out;
}
